import { TableRow } from "@carbon6/c6-frontend-design";
import type { LatencyOperationMetricsResp } from "@/api/observability";
import { compareNumericNullLast } from "@/components/table/compareNumeric";
import { describeLatencySurface } from "./observabilitySurface";
import {
  EMPTY_TOKEN,
  formatCount,
  formatMillis,
  formatOptional,
  formatOptionalAt,
  formatRatio,
  type MetricUnitView,
} from "./observabilityModel";

/** 정렬용 원시값을 따로 가지는 열이다. 나머지(표면·operation·request_id)는 문자열 비교로 충분하다. */
const NUMERIC_COLUMNS = ["calls", "errors", "p50", "p95", "observedMax"] as const;
type NumericColumn = (typeof NUMERIC_COLUMNS)[number];

function isNumericColumn(key: string): key is NumericColumn {
  return (NUMERIC_COLUMNS as readonly string[]).includes(key);
}

/** 큰 수치와 보조 한 줄이다. hotspot 표와 셀 모양이 같아서 같은 컴포넌트가 그린다. */
function composite(value: string, hint = ""): MetricUnitView {
  return { value, hint, peakAt: "" };
}

/**
 * 오류 칸이다. 건수가 값이고, 비율이 보조다.
 *
 * 0 건이면 비율을 적지 않는다. "0건 · 0.0%" 는 같은 말을 두 번 한다. 또한 오류가 있는 행을
 * 눈으로 찾을 때 0 행의 비율이 방해가 된다.
 */
export function describeErrors(count: number, rate: number): MetricUnitView {
  return count === 0 ? composite("0") : composite(formatCount(count), formatRatio(rate));
}

/**
 * 실측 최대 칸이다. 소요 시간이 값이고, 그 호출을 관측한 시각이 보조다.
 *
 * 값이 없을 수 있다. 다음 경우다:
 * - 수집 구간이 2시간 ring 을 넘어 exemplar 가 만료됐다.
 * - 아직 한 tick 도 지나지 않았다.
 * 이 값은 0 이 아니라 미상이다. 그래서 빈 칸으로 표시한다.
 */
export function describeObservedMax(
  observedMax: LatencyOperationMetricsResp["observed_max"],
): MetricUnitView {
  if (observedMax === null) {
    return composite(EMPTY_TOKEN);
  }
  return composite(
    formatMillis(observedMax.duration_ms),
    formatOptionalAt(observedMax.observed_at),
  );
}

/**
 * 지연 표의 한 행이다. surface·operation 하나의 창 요약이다.
 *
 * p50/p95 는 histogram bucket 의 **상한**이다. 그래서 그보다 정밀한 값을 주장하지 않는다.
 * 이 뜻은 열 머리글의 툴팁이 설명한다. 행마다 반복하지 않는다.
 */
export class LatencyTableRow extends TableRow {
  readonly surface: string;
  readonly operation: string;
  readonly calls: string;
  readonly errors: MetricUnitView;
  readonly p50: string;
  readonly p95: string;
  readonly observedMax: MetricUnitView;
  /** 요청을 받은 쪽 행과 보낸 쪽 행을 연결하는 조인 키다. 이 화면의 핵심 동작이라 칸을 따로 준다. */
  readonly requestId: string;

  private readonly sortValues: Readonly<Record<NumericColumn, number | null>>;

  constructor(metrics: LatencyOperationMetricsResp) {
    super(null);
    this.surface = describeLatencySurface(metrics.surface);
    this.operation = metrics.operation;
    this.calls = formatCount(metrics.count);
    this.errors = describeErrors(metrics.error_count, metrics.error_rate);
    this.p50 = formatOptional(metrics.p50_ms, formatMillis);
    this.p95 = formatOptional(metrics.p95_ms, formatMillis);
    this.observedMax = describeObservedMax(metrics.observed_max);
    this.requestId = metrics.observed_max?.request_id ?? EMPTY_TOKEN;
    this.sortValues = {
      calls: metrics.count,
      errors: metrics.error_count,
      p50: metrics.p50_ms,
      p95: metrics.p95_ms,
      observedMax: metrics.observed_max?.duration_ms ?? null,
    };
  }

  /** 정렬용 원시값이다. 기본 순서를 만드는 코드도 이 값을 쓴다. */
  get observedMaxMs(): number | null {
    return this.sortValues.observedMax;
  }

  getRowId(): string {
    return `${this.surface} ${this.operation}`;
  }

  // 오름차순 기준이다. 내림차순은 DataTable 이 부호를 바꾼다.
  override compare(other: this, columnKey: string): number {
    if (isNumericColumn(columnKey)) {
      return compareNumericNullLast(this.sortValues[columnKey], other.sortValues[columnKey]);
    }
    return super.compare(other, columnKey);
  }

  // 서버가 이미 surface 별 상위 250 으로 줄였다. 화면은 더 거르지 않는다.
  canShow(): boolean {
    return true;
  }

  // 읽기 전용 목록이라 선택 기능이 없다.
  canSelect(): boolean {
    return false;
  }
}

/**
 * 응답을 표 행으로 바꾸고 **실측 최대 내림차순**으로 다시 정렬한다.
 *
 * 서버 순서는 호출 수 내림차순이다. 그대로 두면 "드물지만 30초" 인 행이 수백 행 아래에
 * 가려진다. 이 화면은 그런 행을 찾으려고 만들었다. 실측 최대가 없는 행은 뒤로 보낸다.
 * 동률이면 관측 시각이 이른 행을 앞에 둔다. 같은 값이 여러 번 나오면, 처음 그 값에 도달한
 * 시각이 원인을 찾는 출발점이기 때문이다.
 */
export function createLatencyRows(
  operations: readonly LatencyOperationMetricsResp[],
): LatencyTableRow[] {
  return operations.map((metrics) => new LatencyTableRow(metrics)).sort(byObservedMaxDesc);
}

/**
 * 실측 최대 내림차순으로 비교한다. 빈 값은 뒤에 둔다.
 *
 * `compareNumericNullLast` 의 인자 순서를 바꿔 쓰지 않는다. 그 함수는 오름차순 기준이다.
 * 인자 순서를 바꾸면 "빈 값은 뒤" 규칙도 반대가 되어, 값 없는 행이 맨 앞에 온다.
 */
function byObservedMaxDesc(left: LatencyTableRow, right: LatencyTableRow): number {
  const leftMs = left.observedMaxMs;
  const rightMs = right.observedMaxMs;
  if (leftMs === null && rightMs === null) {
    return left.getRowId().localeCompare(right.getRowId());
  }
  if (leftMs === null) {
    return 1;
  }
  if (rightMs === null) {
    return -1;
  }
  if (leftMs === rightMs) {
    return left.getRowId().localeCompare(right.getRowId());
  }
  return rightMs - leftMs;
}
